"use strict";

import {
    BAD_REQUEST,
    FORBIDDEN,
    NOT_FOUND,
    UNPROCESSABLE_ENTITY,
} from "./common/common/common.constants";
import mixpanel from "mixpanel-browser";
import {
    addBreadcrumb,
    captureMessage,
    captureException,
} from "@sentry/browser";
import _ from "lodash";
import { cookiesRequiredState } from "./base.routes";
import { homescreenState } from "./base.routes";
import { RejectType } from "@uirouter/core/lib-esm/transition/rejectFactory";

/* @ngInject */
export default function setup(
    $urlRouter,
    $location,
    $anchorScroll,
    AccountService,
    $q,
    AuthService,
    authManager,
    ToastService,
    ManagementMembershipService,
    GrowerManagementMembershipService,
    $transitions,
    localStorageService,
    $state,
    $urlService,
    $document,
    BeamsService,
    $rootScope,
    $window,
    GrowerMembershipService,
) {
    if (
        !localStorageService.isSupported ||
        !localStorageService.cookie.isSupported
    ) {
        $state.go(cookiesRequiredState);
    }
    let hijacked = $location.search().hijacked;
    if (hijacked) {
        $location.search("hijacked", null);
        localStorageService.set("hijacked", true);
    }
    AuthService.start()
        .then(
            function (token) {
                addBreadcrumb({
                    category: "auth",
                    message: "Picked Token: " + token,
                });
                AuthService.setRefreshToken(token);
                AuthService.setLoggedInFlag();
                return AuthService.getAuthenticatedUserSid().then(
                    function (userSid) {
                        AccountService.retrieve(userSid).then(
                            function (account) {
                                account.updateSentry();
                                account.updateMixpanel();
                                account.updateLanguage();
                                account.updateIterate();
                            },
                        );
                        BeamsService.check(userSid);
                        let growerManagementsResponse =
                            GrowerManagementMembershipService.list(
                                {
                                    user__sid: userSid,
                                    active: true,
                                },
                                {
                                    expand: ["permissions"],
                                },
                            );

                        let growerMembershipsResponse =
                            GrowerMembershipService.list({
                                user__sid: userSid,
                            });

                        return $q
                            .all([
                                growerManagementsResponse,
                                growerMembershipsResponse,
                            ])
                            .then(function (responses) {
                                return AccountService.updatePermissions(
                                    ...responses,
                                );
                            });
                    },
                    function () {
                        addBreadcrumb({
                            category: "auth",
                            message: "Could not get user sid from token",
                        });
                        AuthService.logout();
                        captureMessage(
                            "Logging out due to unable to get user sid from token",
                        );
                        BeamsService.stop();
                    },
                );
            },
            function (reason) {
                addBreadcrumb({
                    category: "auth",
                    message: reason,
                });
                AuthService.logout();
                BeamsService.stop();
            },
        )
        .finally(function () {
            $urlRouter.sync();
            $urlRouter.listen();
        });

    $transitions.onEnter({}, function (transition) {
        const toState = transition.to();
        if (toState.external) {
            transition
                .injector()
                .getAsync("redirect")
                .then(function (redirect) {
                    $window.open(redirect, "_self");
                });
            return false;
        } else {
            addBreadcrumb({
                category: "navigation",
                message: "Transition started to " + toState.name,
            });
        }
        return true;
    });

    $transitions.onFinish({}, function (transition) {
        $rootScope.previousTransition = transition;
    });

    $transitions.onSuccess({}, function (transition) {
        let state = transition.to();
        addBreadcrumb({
            category: "navigation",
            message: "Transition finshed to " + state.name,
        });
        let mixpanelEvent = _.get(state, "data.mixpanelEvent");
        if (mixpanelEvent) {
            let mixpanelEventProperties = _.get(
                state,
                "data.mixpanelEventProperties",
                {},
            );
            if (_.startsWith(state.name, "app.grower.grower.")) {
                mixpanelEventProperties.company_sid =
                    transition.params().grower_sid;
            }
            let resolvedMixpanelProperties = {};
            _.forEach(mixpanelEventProperties, function (resolvePath, name) {
                let [obj, path] = resolvePath.split(".", 2);
                let resolvedObject = transition.injector().get(obj);
                resolvedMixpanelProperties[name] = _.get(resolvedObject, path);
            });
            mixpanel.track(mixpanelEvent, resolvedMixpanelProperties);
        }

        let elementID = transition.params().scrollTo;
        if (elementID) {
            $location.hash(elementID);
            $anchorScroll(elementID);
        }

        $rootScope.showSidebar = false;
    });

    $transitions.onError({}, (transition) => {
        const err = transition.error();
        const type = err?.type;
        const info = err?.detail || {};

        // --- 1) Code-split failure ---------------------------------------------
        if (info.name === "ChunkLoadError") {
            captureException("Handling ChunkLoadError", {
                contexts: {
                    error: {
                        from_state: transition.from().name,
                        to_state: transition.to().name,
                        status: info.status,
                        data: JSON.stringify(info.data),
                    },
                },
            });
            $window.location.reload(true);
            return;
        }

        // Only act on real routing errors
        if (type !== RejectType.INVALID && type !== RejectType.ERROR) {
            return;
        }

        // --- 2) Server-driven redirect ------------------------------------------
        if (info.redirectTo) {
            return $state.go(info.redirectTo.state, info.redirectTo.params);
        }

        // --- 3) Expected API status codes ---------------------------------------
        if (
            [BAD_REQUEST, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY].includes(
                info.status,
            )
        ) {
            mixpanel.track("Transition error");
            captureException(`Transition Error: ${info.status}`, {
                contexts: {
                    error: {
                        from_state: transition.from().name,
                        to_state: transition.to().name,
                        status: info.status,
                        data: JSON.stringify(info.data),
                    },
                },
            });
            $state.go(homescreenState);
            return;
        }

        // --- 4) Anything else ----------------------------------------------------
        captureException(info, {
            contexts: {
                error: {
                    from_state: transition.from().name,
                    to_state: transition.to().name,
                },
            },
        });
        $state.go(homescreenState, {}, { reload: true });
    });

    $state.defaultErrorHandler(function () {
        // This is empty to prevent the default error handler from logging,
        // instead we're handling them in .onError method of the transition service
    });

    $window.addEventListener("storage", (event) => {
        if (event.key === "ls.refresh") {
            if (event.newValue !== null) {
                let oldUserSid = AuthService.getAuthenticatedUserSid(
                    event.oldValue,
                );
                let newUserSid = AuthService.getAuthenticatedUserSid(
                    event.newValue,
                );
                $q.all([oldUserSid, newUserSid]).then(function ([
                    oldSid,
                    newSid,
                ]) {
                    if (oldSid !== newSid) {
                        addBreadcrumb({
                            category: "auth",
                            message: "User was changed in another tab",
                            data: { oldSid, newSid },
                        });
                        $state.go(homescreenState, {}, { reload: true });
                    }
                });
            } else {
                AuthService.logout();
            }
        }
    });

    $rootScope.$on(
        "LocalStorageModule.notification.setitem",
        function (event, opts) {
            addBreadcrumb({
                category: "auth",
                message: "Localstorage set",
                data: opts,
            });
        },
    );
    $rootScope.$on(
        "LocalStorageModule.notification.removeitem",
        function (event, opts) {
            addBreadcrumb({
                category: "auth",
                message: "Localstorage remove",
                data: opts,
            });
        },
    );

    AuthService.getAuthenticatedUserSid().then(
        function (userSid) {
            mixpanel.identify(userSid);
            mixpanel.track("App initialized");
        },
        function () {},
    );
}
